import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { useI18n } from '../../i18n';
import UploadReceiptImageWeb from './components/UploadReceiptImageWeb';
import zhipuOCRService from '../../services/zhipuOCRService';
import ReceiptBasicInfoWeb from './components/ReceiptBasicInfoWeb';
import CategorySelectorWeb from './components/CategorySelectorWeb';
import PrivateSplitWeb from './components/PrivateSplitWeb';
import { MODULE_COLORS } from '@cubemeet/core/theme';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import { uploadPrivateReceipt } from '@cubemeet/core/store/slices/expenseReceiptsSlice';
import { supabase } from '@cubemeet/core/services/supabase';
import ModuleHeader from '../../components/common/ModuleHeader';

export interface UploadReceiptWebPageProps {
  groupId?: string;
  onClose: () => void;
}

const categories = [
  { value: 'transport', label: 'Transport', icon: 'bus', color: '#3498db' },
  { value: 'accommodation', label: 'Accommodation', icon: 'hotel', color: '#9b59b6' },
  { value: 'food', label: 'Food', icon: 'restaurant', color: '#e74c3c' },
  { value: 'flight', label: 'Flight', icon: 'flight', color: '#2980b9' },
  { value: 'taxi', label: 'Taxi', icon: 'local-taxi', color: '#f39c12' },
  { value: 'attractions', label: 'Attractions', icon: 'place', color: '#27ae60' },
  { value: 'shopping', label: 'Shopping', icon: 'shopping-bag', color: '#1abc9c' },
  { value: 'entertainment', label: 'Entertainment', icon: 'local-play', color: '#e67e22' },
  { value: 'insurance', label: 'Insurance', icon: 'security', color: '#8e44ad' },
  { value: 'visa', label: 'Visa', icon: 'card-membership', color: '#34495e' },
  { value: 'communication', label: 'Communication', icon: 'phone', color: '#16a085' },
  { value: 'general', label: 'General', icon: 'more-horiz', color: '#95a5a6' },
];

const UploadReceiptWebPage: React.FC<UploadReceiptWebPageProps> = ({ groupId, onClose }) => {
  const { translations } = useI18n();
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const t = useCallback((key: string) => {
    try {
      const segments = key.split('.');
      let v: any = translations ?? {};
      for (const s of segments) {
        if (v && typeof v === 'object' && s in v) v = v[s]; else return key;
      }
      return typeof v === 'string' ? v : key;
    } catch (e) {
      return key;
    }
  }, [translations]);

  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrInfo, setOcrInfo] = useState<{ confidence?: number | null } | null>(null);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('CNY');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState<string>('general');
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);

  // Normalize OCR currency outputs into ISO 4217 codes when possible.
  const normalizeCurrency = (raw?: any) => {
    // Avoid blindly mapping ambiguous symbols like '$' to USD.
    // Prefer explicit ISO codes or symbol+country markers (e.g. 'HK$').
    if (!raw && raw !== 0) return undefined;
    const s = String(raw).trim();
    if (!s) return undefined;
    const map: Record<string, string> = {
      'US$': 'USD',
      'USD': 'USD',
      'HK$': 'HKD',
      'HKD': 'HKD',
      '¥': 'CNY',
      '￥': 'CNY',
      'CNY': 'CNY',
      'NT$': 'TWD',
      'TWD': 'TWD',
      'EUR': 'EUR',
      '€': 'EUR',
      'GBP': 'GBP',
      '£': 'GBP',
    };
    // direct map
    if (map[s]) return map[s];
    // strip whitespace and common suffix/prefixes
    const cleaned = s.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (/^[A-Z]{3}$/.test(cleaned)) return cleaned;
    // symbol-only values (like "$") are ambiguous; return undefined so caller
    // can decide (for example, prefer travel.currency or leave as-is).
    if (/^\$+$/.test(s)) return undefined;
    if (s.startsWith('HK')) return 'HKD';
    if (s.indexOf('NT') >= 0) return 'TWD';
    return s;
  };

  // Sanitize OCR returned fields (avoid using phone numbers as title, prefer merchant)
  const sanitizeOCRData = (d: any) => {
    const out: any = { ...d };
    try {
      // If title looks like a phone number (6+ digits, possibly separated by - or spaces), prefer merchant
      if (typeof out.title === 'string') {
        const digits = out.title.replace(/[^0-9]/g, '');
        if (digits.length >= 6 && /^[0-9\-\s()+]+$/.test(out.title)) {
          // fallback to merchant if available
          if (out.merchant && typeof out.merchant === 'string' && out.merchant.trim()) {
            out.title = out.merchant;
          } else {
            // otherwise clear title so UI won't show hotline as title
            out.title = undefined;
          }
        }
      }
    } catch (e) {
      // ignore
    }
    return out;
  };

  // split state
  const [splitEnabled, setSplitEnabled] = useState<boolean>(false);
  const [splitMode, setSplitMode] = useState<'equal' | 'exact' | 'percentage' | 'shares'>('equal');

  // Try to pick up real members from the travels store when groupId is present
  const travelsState = useSelector((state: RootState) => (state as any).travels ?? {});
  const computedMembers = useMemo(() => {
    try {
      const ct = travelsState.currentTravel;
      if (groupId && ct && ct.id === groupId && Array.isArray(ct.members)) {
        return ct.members.map((m: any) => ({ id: m.user_id, name: m.profile?.display_name || m.user_id, avatar: m.profile?.avatar_url || null }));
      }
    } catch (e) {
      // ignore
    }
    // Fallback placeholder members for standalone usage
    return [
      { id: 'u1', name: 'Raymond', avatar: 'https://images.unsplash.com/photo-1502685104226-ee32379fefbe?w=256&h=256&fit=crop' },
      { id: 'u2', name: 'Cube Meet', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=256&h=256&fit=crop' },
    ];
  }, [groupId, travelsState.currentTravel]);

  // use computedMembers directly so updates from Redux (real group members) flow through
  const members = computedMembers;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [splitInputs, setSplitInputs] = useState<Record<string, string>>({});
  const [percentageValues, setPercentageValues] = useState<Record<string, number>>({});
  const [shareValues, setShareValues] = useState<Record<string, number>>({});

  // Initialize split-related maps whenever computed members change
  useEffect(() => {
    const ids = computedMembers.map(m => m.id);
    setSelectedIds(ids);
    const inputs: Record<string, string> = {};
    const perc: Record<string, number> = {};
    const shares: Record<string, number> = {};
    const per = Math.floor(100 / Math.max(1, ids.length));
    ids.forEach((id, i) => {
      inputs[id] = '';
      perc[id] = per;
      shares[id] = 1;
    });
    setSplitInputs(inputs);
    setPercentageValues(perc);
    setShareValues(shares);
  }, [computedMembers]);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const url = URL.createObjectURL(file);
      setPreviewSrc(url);
      // Trigger OCR on selected file
      (async () => {
        try {
          setOcrLoading(true);
          setOcrInfo(null);
          setOcrError(null);
          setSelectedFile(file);
          const result = await zhipuOCRService.recognizeReceipt({ file, name: file.name, type: file.type, size: file.size });
          // Log OCR raw result for debugging (helps determine if AI returned incomplete data)
          try { console.log('[OCR] raw result', { file: file.name, time: new Date().toISOString(), result }); } catch (e) {}

          if (result.success && result.data) {
            // sanitize OCR data (avoid hotline as title, prefer merchant)
            const raw = result.data;
            const d = sanitizeOCRData(raw);
            setOcrInfo({ confidence: d.confidence ?? null });
            // Autofill fields when available - collect which fields will be set for debug
            const applied: Record<string, any> = {};
            // use merchant as title if present (prompt also requests this)
            if (d.merchant && (!d.title || d.title === undefined)) {
              d.title = d.merchant;
            }
            if (d.title) { setTitle(d.title); applied.title = d.title; }
            if (typeof d.amount === 'number') { setAmount(String(d.amount)); applied.amount = d.amount; }
            if (d.currency) { const norm = normalizeCurrency(d.currency); setCurrency(norm || String(d.currency)); applied.currency = norm || d.currency; }
            if (d.date) { setDate(d.date); applied.date = d.date; }
            if (d.category) { setCategory(d.category); applied.category = d.category; }
            try { console.log('[OCR] applying fields', { file: file.name, time: new Date().toISOString(), applied }); } catch (e) {}
          } else {
            console.warn('OCR did not return useful data', result.error);
            setOcrError(result.error || 'OCR returned no data');
          }
        } catch (err: any) {
          console.error('OCR error', err);
          setOcrError(err?.message || String(err));
        } finally {
          setOcrLoading(false);
        }
      })();
    }
  }, []);

  const toggleParticipant = (id: string) => {
    setSelectedIds(prev => {
      const nextSelected = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];

      // If split is enabled, rebalance percentage/share values to keep total valid
      if (splitEnabled) {
        if (splitMode === 'percentage') {
          // distribute 100% across selected
          setPercentageValues(prevPerc => {
            const next: Record<string, number> = {};
            const selectedMembers = members.filter(m => nextSelected.includes(m.id));
            const count = selectedMembers.length;
            let remaining = 100;
            selectedMembers.forEach((member, idx) => {
              const value = count === 0 ? 0 : (idx === count - 1 ? Number(remaining.toFixed(1)) : Number((100 / count).toFixed(1)));
              remaining = Number((remaining - value).toFixed(1));
              next[member.id] = value;
            });
            members.forEach(m => { if (!nextSelected.includes(m.id)) next[m.id] = 0; });
            // recalc amounts
            calculateSplitAmounts('percentage', next);
            return next;
          });
        } else if (splitMode === 'shares') {
          setShareValues(prevShares => {
            const next: Record<string, number> = { ...prevShares };
            // ensure selected ones have at least 1, others 0
            members.forEach(m => { next[m.id] = nextSelected.includes(m.id) ? (next[m.id] && next[m.id] > 0 ? next[m.id] : 1) : 0; });
            calculateSplitAmounts('shares', next);
            return next;
          });
        }
      }

      return nextSelected;
    });
  };

  const dispatch = useDispatch<AppDispatch>();

  const onSubmit = () => {
    (async () => {
      try {
        // Basic validation
        const total = parseAmountValue(amount);
        if (isNaN(total) || total <= 0) {
          window.alert('Please enter a valid amount before saving.');
          return;
        }
        if (!groupId) {
          window.alert('Missing groupId for expense.');
          return;
        }

        // Show a lightweight loading indicator
        if (typeof window !== 'undefined') {
          // using alert as temporary UX; in-app you may want a proper toast
          // but avoid blocking the async flow
          console.log('[UploadReceipt] submitting...');
        }

        // 1) upload file via Cloudflare Worker to R2 (match mobile app behavior)
        let receiptUrl: string | null = null;
        let workerFailed = false;
        if (selectedFile) {
          try {
            // resolve worker upload URL from various env sources
            const globalEnv = (globalThis as any)?.__CUBEMEET_ENV__;
            const workerUrl = (globalEnv && globalEnv.CLOUDFLARE_WORKER_UPLOAD_URL) || (process && (process.env?.CLOUDFLARE_WORKER_UPLOAD_URL as string)) || (typeof import.meta !== 'undefined' ? (import.meta as any).env?.VITE_CLOUDFLARE_WORKER_UPLOAD_URL : undefined) || '';

              if (!workerUrl) {
                console.warn('Cloudflare worker upload URL not configured; skipping worker upload.');
                workerFailed = true;
              } else {
              // Try to include uploader id (userId) so the Worker can validate/store metadata
              const { data: authData } = await supabase.auth.getUser();
              const uploaderId = authData?.user?.id ?? null;

              const fd = new FormData();
              fd.append('file', selectedFile);
              fd.append('filename', selectedFile.name);
              // Worker expects userId and itemId fields
              if (uploaderId) fd.append('userId', String(uploaderId));
              if (groupId) fd.append('itemId', String(groupId)); else fd.append('itemId', 'temp');

              // wrap fetch with a timeout to avoid hanging requests
              const fetchWithTimeout = (url: string, opts: any, timeoutMs = 15000) => {
                return Promise.race([
                  fetch(url, opts),
                  new Promise((_, reject) => setTimeout(() => reject(new Error('Worker upload timeout')), timeoutMs)),
                ]);
              };

              try {
                const resp = await fetchWithTimeout(workerUrl, { method: 'POST', body: fd }, 15000) as Response;
                if (!resp.ok) {
                  const text = await resp.text().catch(() => '');
                  console.warn('Worker upload failed', resp.status, resp.statusText, text);
                  workerFailed = true;
                } else {
                const json = await resp.json().catch(() => ({}));
                // worker may return different shapes; try common keys
                receiptUrl = json.url || json.path || (json.result && json.result.url) || (json.publicURL || json.publicUrl) || null;
                // if worker returns an object with bucket/path, try to build URL from endpoint env
                if (!receiptUrl && json.bucket && json.key) {
                  const endpoint = (globalEnv && globalEnv.CLOUDFLARE_R2_ENDPOINT) || (process && (process.env?.CLOUDFLARE_R2_ENDPOINT as string)) || (typeof import.meta !== 'undefined' ? (import.meta as any).env?.VITE_CLOUDFLARE_R2_ENDPOINT : undefined) || '';
                  if (endpoint) {
                    receiptUrl = `${endpoint}/${json.key}`;
                  }
                }
                }
              } catch (err) {
                console.warn('Worker upload exception or timeout', err);
                workerFailed = true;
              }
            }
          } catch (err) {
            console.warn('Receipt worker upload exception', err);
            workerFailed = true;
          }
        }

        // Fallback: if worker upload failed or didn't produce a URL, try Supabase Storage upload (dev-friendly)
        if ((!receiptUrl || workerFailed) && selectedFile) {
          try {
            console.log('[UploadReceipt] worker upload failed, falling back to Supabase Storage');
            const filename = `receipts/${Date.now()}_${selectedFile.name}`;
            const { data: uploadData, error: uploadErr } = await supabase.storage.from('receipts').upload(filename, selectedFile as any, { upsert: true });
            if (uploadErr) {
              console.warn('Supabase receipt upload failed', uploadErr);
            } else if (uploadData?.path) {
              const { publicURL } = supabase.storage.from('receipts').getPublicUrl(uploadData.path);
              receiptUrl = publicURL ?? receiptUrl;
            }
          } catch (err) {
            console.warn('Supabase receipt upload exception', err);
          }
        }

  // 2) ensure splitInputs are computed
  calculateSplitAmounts(splitMode);

        // Build participants payload
        const selectedMembers = members.filter(m => selectedIds.includes(m.id));
        const participants = selectedMembers.map(m => {
          const assigned = splitInputs[m.id] ?? '0.00';
          const numeric = parseAmountValue(assigned);
          return {
            user_id: m.id,
            amount: Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : 0,
            percentage: percentageValues[m.id] ?? 0,
            shares: shareValues[m.id] ?? 0,
          };
        });

        // If private split enabled, use shared uploadPrivateReceipt flow which
        // manages inserting into `group_private_receipts` and `group_private_receipt_members`.
        if (splitEnabled) {
          try {
            const { data: authData } = await supabase.auth.getUser();
            const uploaderId = authData?.user?.id ?? null;

            // compute exchange_rate when converted amount is provided to satisfy DB constraints
            const amt = Number(total.toFixed(2));
            const convRaw = convertedAmount !== null && convertedAmount !== undefined ? Number(convertedAmount) : undefined;
            const conv = Number.isFinite(convRaw) && convRaw > 0 ? convRaw : undefined;
            const exchangeRateValue = Number.isFinite(conv) && amt > 0 ? Number((conv / amt).toFixed(6)) : undefined;
            const payload: any = {
              group_id: groupId!,
              title: title || null,
              description: null,
              amount: amt,
              currency: currency || null,
              // Only include exchange metadata when both amount and converted amount are valid (>0).
              converted_amount: conv ?? null,
              exchange_rate: exchangeRateValue ?? null,
              // Use date-only string for exchange_rate_date to match DB expectations when present
              exchange_rate_date: exchangeRateValue ? new Date().toISOString().split('T')[0] : null,
              is_manual_rate: false,
              category: category || null,
              expense_date: date || null,
              file: selectedFile ? { uri: '', name: selectedFile.name, type: selectedFile.type, size: selectedFile.size } : undefined,
              split_mode: splitMode,
              participants: selectedIds.map(id => ({ user_id: id, amount: splitInputs[id] ? parseFloat(splitInputs[id]) : null, percentage: percentageValues[id] ?? null, share: shareValues[id] ?? null })),
            };

            // Debug: log payload shape to help diagnose DB constraint failures
            try { console.debug('[UploadReceipt] private payload', payload); } catch (e) {}

            const privateResult = await dispatch(uploadPrivateReceipt(payload)).unwrap();
            console.log('[UploadReceipt] private upload result', privateResult);
            window.alert('Private receipt saved successfully');
            onClose();
            return;
          } catch (err: any) {
            console.error('uploadPrivateReceipt failed', err);
            window.alert(err?.message || 'Failed to save private receipt');
            return;
          }
        }

        // 3) insert expense row into the app's canonical `expense_receipts` table
        // Use the expected column names: `expense_date` (not `date`) and include uploader_id from auth
        const { data: authData } = await supabase.auth.getUser();
        const uploaderId = authData?.user?.id ?? null;

        const expenseRow: any = {
          group_id: groupId,
          uploader_id: uploaderId,
          title: title || null,
          amount: Number(total.toFixed(2)),
          currency: currency || null,
          expense_date: date || null,
          category: category || null,
          receipt_url: receiptUrl || null,
          // include file metadata fields expected by backend
          file_name: selectedFile?.name || null,
          file_size: selectedFile ? (selectedFile.size as any) : null,
          file_type: selectedFile?.type || null,
        };

        const { data: insertData, error: insertErr } = await supabase.from('expense_receipts').insert(expenseRow).select().single();
        let expenseId: any = null;
        if (insertErr || !insertData) {
          console.error('Failed to insert into `expense_receipts` table', insertErr);
          window.alert('Failed to save expense.');
          return;
        }
        expenseId = insertData.id;

        // 4) Participant/split rows: the project stores private-split receipts in different tables
        // (see `group_private_receipts` / `group_private_receipt_members`) and the public
        // `expense_participants` table may not exist in all deployments. To avoid 404/Bad Request
        // errors from incorrect table/column names, we skip inserting participants here.
        // TODO: if you want split participants persisted, call the server-side helper or
        // use the `uploadPrivateReceipt` flow which manages `group_private_receipts` and
        // `group_private_receipt_members` properly.
        if (participants.length > 0) {
          console.log('[UploadReceipt] participants prepared but not inserted by web uploader (skipped):', participants);
        }

        window.alert('Expense saved successfully');
        onClose();
      } catch (e: any) {
        console.error('Save error', e);
        window.alert('An unexpected error occurred while saving.');
      }
    })();
  };

  const handleInputChange = useCallback((id: string, v: string) => {
    setSplitInputs(prev => ({ ...prev, [id]: v }));
  }, []);

  const handleValueChange = (id: string, v: number) => {
    // Update percentage or share values and recompute split inputs immediately
    if (splitMode === 'percentage') {
      setPercentageValues(prev => {
        const next: Record<string, number> = { ...prev, [id]: Math.max(0, v) };
        // normalize so selected members sum to 100
        const selected = members.filter(m => selectedIds.includes(m.id)).map(m => m.id);
        const selectedSum = selected.reduce((s, sid) => s + (typeof next[sid] === 'number' ? next[sid] : 0), 0);
        let normalized = { ...next };
        if (selected.length > 0) {
          const diff = Number((100 - selectedSum).toFixed(1));
          if (Math.abs(diff) >= 0.1) {
            // distribute diff proportionally among other selected members (excluding id) if they have >0 weight
            const others = selected.filter(sid => sid !== id);
            const othersSum = others.reduce((s, sid) => s + (typeof next[sid] === 'number' ? next[sid] : 0), 0);
            if (others.length > 0) {
              if (othersSum > 0) {
                others.forEach(sid => {
                  const share = (typeof next[sid] === 'number' ? next[sid] : 0) / othersSum;
                  normalized[sid] = Number((next[sid] + diff * share).toFixed(1));
                });
              } else {
                // spread equally
                const each = Number((diff / others.length).toFixed(1));
                others.forEach(sid => { normalized[sid] = Number((next[sid] + each).toFixed(1)); });
              }
            } else {
              // only one selected, set it to 100
              normalized[id] = 100;
            }
          }
        }
        calculateSplitAmounts('percentage', normalized);
        return normalized;
      });
    } else {
      setShareValues(prev => {
        const next = { ...prev, [id]: Math.max(0, v) };
        if (splitMode === 'shares') calculateSplitAmounts('shares', next);
        return next;
      });
    }
  };

  // helpers for split calculation (ported from mobile)
  const parseAmountValue = (val?: string | number) => {
    if (val === undefined || val === null) return NaN;
    const s = String(val).trim();
    if (!s) return NaN;
    // remove non-numeric except dot and minus
    const cleaned = s.replace(/[^0-9.\-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  };

  const getTotalAmountForSplit = () => {
    // Prefer convertedAmount (target currency) when available; otherwise parse original amount
    const total = (typeof convertedAmount === 'number' && !isNaN(convertedAmount)) ? Number(convertedAmount) : parseAmountValue(amount);
    if (isNaN(total)) return NaN;
    return Number(total.toFixed(2));
  };

  const calculateSplitAmounts = (modeToUse: 'equal' | 'exact' | 'percentage' | 'shares', valuesOverride?: Record<string, number>) => {
    const total = getTotalAmountForSplit();
    const selectedMembers = members.filter(m => selectedIds.includes(m.id));
    const baseAmounts: Record<string, string> = {};
    members.forEach(m => { baseAmounts[m.id] = ''; });

    if (!splitEnabled || isNaN(total) || total <= 0 || selectedMembers.length === 0) {
      setSplitInputs(baseAmounts);
      return;
    }

    if (modeToUse === 'exact') {
      setSplitInputs(prev => {
        const next: Record<string, string> = { ...baseAmounts };
        selectedMembers.forEach(member => { next[member.id] = prev[member.id] ?? ''; });
        return next;
      });
      return;
    }

    let weights: number[];
    if (modeToUse === 'equal') {
      weights = selectedMembers.map(() => 1);
    } else {
      const valueMap = valuesOverride ?? (modeToUse === 'percentage' ? percentageValues : modeToUse === 'shares' ? shareValues : {});
      weights = selectedMembers.map(member => {
        const raw = valueMap[member.id];
        return (typeof raw === 'number' && raw > 0) ? raw : 0;
      });
    }

    let totalWeight = weights.reduce((s, w) => s + w, 0);
    if ((modeToUse === 'percentage' || modeToUse === 'shares') && totalWeight <= 0) {
      weights = selectedMembers.map(() => 1);
      totalWeight = weights.length;
    }

    if (totalWeight <= 0) {
      weights = selectedMembers.map(() => 1);
      totalWeight = weights.length;
    }

    let remaining = total;
    const computed: Record<string, string> = { ...baseAmounts };

    selectedMembers.forEach((member, idx) => {
      let amountNumber: number;
      if (idx === selectedMembers.length - 1) {
        amountNumber = Number(remaining.toFixed(2));
      } else {
        const weight = weights[idx];
        const fraction = totalWeight > 0 ? weight / totalWeight : 0;
        const rawAmount = total * fraction;
        amountNumber = Math.floor(rawAmount * 100) / 100;
      }
      remaining = Number((remaining - amountNumber).toFixed(2));
      if (remaining < 0) remaining = 0;
      computed[member.id] = amountNumber.toFixed(2);
    });

    setSplitInputs(computed);
  };

  // Recalculate when split-related inputs change (debounced by 150ms similar to mobile)
  useEffect(() => {
    if (!splitEnabled) return;
    if (splitMode === 'exact') {
      setSplitInputs(prev => {
        const next: Record<string, string> = {};
        members.forEach(m => { next[m.id] = selectedIds.includes(m.id) ? (prev[m.id] ?? '') : ''; });
        return next;
      });
      return;
    }

    const timer = setTimeout(() => calculateSplitAmounts(splitMode), 150);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitEnabled, splitMode, amount, convertedAmount, selectedIds, members, percentageValues, shareValues]);

  return (
  <div style={{ ...webStyles.page, paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
      <div style={webStyles.mainContainer}>
        <ModuleHeader
          moduleType="travel"
          title={t('expense.upload.title') || 'Add Expense'}
          subtitle={t('expense.upload.subtitle') || 'Upload receipt and fill expense info'}
          showBackButton={true}
          onBack={onClose}
          rightButton={{ icon: 'done', onPress: onSubmit, style: 'save' }}
          noEdgePadding={true}
        />

        <div style={webStyles.contentBody}>
          <UploadReceiptImageWeb previewSrc={previewSrc} file={selectedFile ?? undefined} onFileChange={onFileChange} onOpenPicker={() => { const el = document.getElementById('upload-input-web') as HTMLInputElement | null; if (el) el.click(); }} ocrLoading={ocrLoading} ocrInfo={ocrInfo} onChangeImage={() => { const el = document.getElementById('upload-input-web') as HTMLInputElement | null; if (el) el.click(); }} />

          {ocrError && (
            <div style={{ marginTop: 8, padding: 12, background: '#fff3f2', borderRadius: 8, border: '1px solid #ffd2cf', color: '#9b1c1c' }}>
              <div style={{ fontWeight: 600 }}>OCR unavailable</div>
              <div style={{ marginTop: 6 }}>{String(ocrError)}</div>
              <div style={{ marginTop: 8 }}>
                <button onClick={async () => {
                  if (!selectedFile) return;
                  setOcrLoading(true);
                  setOcrError(null);
                  try {
                    const r = await zhipuOCRService.recognizeReceipt({ file: selectedFile, name: selectedFile.name, type: selectedFile.type, size: selectedFile.size });
                    if (r.success && r.data) {
                      const d = r.data;
                      setOcrInfo({ confidence: d.confidence ?? null });
                      if (d.title) setTitle(d.title);
                      if (typeof d.amount === 'number') setAmount(String(d.amount));
                      if (d.currency) setCurrency(d.currency);
                      if (d.date) setDate(d.date);
                      if (d.category) setCategory(d.category);
                      setOcrError(null);
                    } else {
                      setOcrError(r.error || 'OCR returned no data');
                    }
                  } catch (e: any) {
                    setOcrError(e?.message || String(e));
                  } finally {
                    setOcrLoading(false);
                  }
                }} style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff' }}>Retry OCR</button>
              </div>
            </div>
          )}

          <ReceiptBasicInfoWeb
            title={title}
            amount={amount}
            currency={currency}
            date={date}
            onTitleChange={setTitle}
            onAmountChange={setAmount}
            onCurrencyChange={setCurrency}
            onDateChange={setDate}
            isManualRate={false}
            targetCurrency={(travelsState.currentTravel && travelsState.currentTravel.currency) || undefined}
            onConvertedChange={(v) => setConvertedAmount(v)}
          />

          <CategorySelectorWeb categories={categories} selected={category} onSelect={setCategory} />

          {/* Private split card rendered below (handles its own switch) */}

          <PrivateSplitWeb
            members={members}
            splitEnabled={splitEnabled}
            onToggleSplit={(v?: boolean) => setSplitEnabled(typeof v === 'boolean' ? v : !splitEnabled)}
            splitMode={splitMode}
            selectedIds={selectedIds}
            splitInputs={splitInputs}
            percentageValues={percentageValues}
            shareValues={shareValues}
            onToggle={toggleParticipant}
            onModeChange={(m) => setSplitMode(m)}
            onInputChange={handleInputChange}
            onValueChange={handleValueChange}
          />

          {/* Bottom action buttons removed for web flow (Save/Cancel handled in header) */}
        </div>
      </div>
    </div>
  );
};

const webStyles: Record<string, React.CSSProperties> = {
  page: { padding: 16, background: '#f8f9fa', minHeight: '100vh', display: 'flex', justifyContent: 'center' },
  mainContainer: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    alignSelf: 'flex-start',
    marginBottom: 20,
  },
  contentBody: { padding: 16 },
  header: { padding: '12px 0', marginBottom: 8 },
  headerSubtitle: { color: '#6b7280', fontSize: 13 },
  actionsRow: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 },
  btn: { padding: '10px 16px', borderRadius: 8, border: '1px solid transparent', cursor: 'pointer' },
  cancelBtn: { background: '#fff', borderColor: '#e5e7eb' },
};

export default UploadReceiptWebPage;
